import React, { useEffect, useState } from "react";
import SideBar from "../components/SideBar";
import DetailsHeaderBar from "../components/DetailsHeaderBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/EditCustomerProfile.css";
import { parentsConsentService } from "../services/parentsConsentService";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
});

function EditParentsConsent() {
  const location = useLocation();
  const navigate = useNavigate();
  const initial = location.state?.consent;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [consent, setConsent] = useState(initial || null);
  const [reviewStatus, setReviewStatus] = useState("pending");
  const [reviewNotes, setReviewNotes] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!initial?.id) {
        setFetching(false);
        return;
      }
      try {
        const fresh = await parentsConsentService.getById(initial.id);
        setConsent(fresh);
        setReviewStatus(fresh.review_status || "pending");
        setReviewNotes(fresh.review_notes || "");
      } catch (error) {
        console.error("Failed to fetch parental consent", error);
        setSnackbarSeverity("error");
        setSnackbarMessage("Failed to load parental consent record");
        setOpenSnackbar(true);
      } finally {
        setFetching(false);
      }
    };
    load();
  }, [initial?.id]);

  const breadcrumbs = [
    { label: "Home", path: "/dashboard" },
    { label: "Manage Parental Consent", path: "/manage-parental-consent" },
    { label: "Edit Parental Consent" },
  ];

  const handleSave = async () => {
    if (!consent?.id) return;
    setLoading(true);
    try {
      await parentsConsentService.review(consent.id, {
        review_status: reviewStatus,
        review_notes: reviewNotes.trim() || null,
      });
      setSnackbarSeverity("success");
      setSnackbarMessage("Parental consent review updated.");
      setOpenSnackbar(true);
      setTimeout(() => {
        navigate("/parental-consent-details", { state: { consent: { ...consent, review_status: reviewStatus, review_notes: reviewNotes } } });
      }, 1200);
    } catch (error) {
      console.error("Failed to save review", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(error?.message || "Failed to update review");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  return (
    <div className="member-details-layout">
      <SideBar />
      <div className="member-details-main">
        <DetailsHeaderBar
          title="Edit Parental Consent"
          subtitle="Update review status and notes (approve/reject/pending)."
          breadcrumbs={breadcrumbs}
        />

        {fetching ? (
          <div className="section">
            <div className="section-content">Loading…</div>
          </div>
        ) : (
          <div className="section">
            <h3>Review Update</h3>
            <div className="section-content">
              <p>
                <strong>Consent ID:</strong> {consent?.id ?? "N/A"}
              </p>
              <p>
                <strong>Child:</strong> {consent?.child_full_name ?? "N/A"}
              </p>

              <div style={{ marginTop: 12, maxWidth: 520 }}>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                  Review status
                </label>
                <select
                  className="filter-select"
                  style={{ marginLeft: 0, width: "100%" }}
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div style={{ marginTop: 12, maxWidth: 720 }}>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                  Review notes
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={5}
                  style={{
                    width: "100%",
                    borderRadius: 10,
                    border: "1px solid rgba(68, 71, 92, 0.3)",
                    padding: 12,
                    fontFamily: "inherit",
                  }}
                  placeholder="Optional notes shown to support resolution/resubmission."
                 maxLength={65535} />
              </div>

              <div className="edit-btn-wrap" style={{ marginTop: 18 }}>
                <button className="edit-btn" onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default EditParentsConsent;

